<template>
  <div>
    <div ref="container"></div>
    <p v-if="loadError" class="red small mt-2 text-center">
      No se pudo cargar la verificación de seguridad{{ errorCode ? ` (${errorCode})` : "" }}.
      <a href="#" @click.prevent="init">Reintentar</a>
    </p>
  </div>
</template>

<script>
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const CALLBACK_NAME = "cfTurnstileOnLoad";
const LOAD_TIMEOUT = 10000;
const MAX_ATTEMPTS = 3;
const RESET_INTERVAL = 295 * 1000;

// Promesa compartida entre instancias para no inyectar el script dos veces.
// Se limpia cuando la carga falla, de modo que un mount posterior pueda reintentar.
let loaderPromise = null;

function injectScript() {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${SCRIPT_URL}?onload=${CALLBACK_NAME}&render=explicit`;
    script.async = true;

    // El script puede cargar sin ejecutar (proxy que altera el Content-Type o
    // trunca la respuesta): en ese caso no se dispara el evento "error" y sin
    // este timeout la promesa quedaría pendiente para siempre.
    const timer = setTimeout(() => {
      script.remove();
      delete window[CALLBACK_NAME];
      reject(new Error("Timeout al cargar Turnstile"));
    }, LOAD_TIMEOUT);

    window[CALLBACK_NAME] = () => {
      clearTimeout(timer);
      resolve();
    };

    script.addEventListener("error", () => {
      clearTimeout(timer);
      script.remove();
      delete window[CALLBACK_NAME];
      reject(new Error("No se pudo descargar Turnstile"));
    });

    document.head.appendChild(script);
  });
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function loadTurnstile() {
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (!loaderPromise) {
    loaderPromise = (async () => {
      let lastError;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          await injectScript();
          return;
        } catch (error) {
          lastError = error;
          if (attempt < MAX_ATTEMPTS) {
            await delay(attempt * 1000);
          }
        }
      }
      loaderPromise = null;
      throw lastError;
    })();
  }
  return loaderPromise;
}

export default {
  name: "TurnstileWidget",
  emits: ["update:modelValue", "error", "expired", "unsupported"],
  props: {
    siteKey: { type: String, required: true },
    modelValue: { type: String, default: "" },
    theme: { type: String, default: "auto" },
    size: { type: String, default: "normal" },
    language: { type: String, default: "auto" },
    action: { type: String, default: "" },
    appearance: { type: String, default: "always" }
  },
  data() {
    return {
      widgetId: null,
      loadError: false,
      errorCode: "",
      resetTimer: null
    };
  },
  mounted() {
    this.init();
  },
  beforeUnmount() {
    this.clearResetTimer();
    this.removeWidget();
  },
  methods: {
    async init() {
      this.loadError = false;
      this.errorCode = "";
      try {
        await loadTurnstile();
        // El script tarda y el usuario puede haberse ido a otra vista mientras tanto:
        // ahi el contenedor ya no existe y render() reventaria con un ref indefinido.
        if (!this.$refs.container) {
          return;
        }
        this.removeWidget();
        this.render();
      } catch (error) {
        this.loadError = true;
        this.$emit("error", error.message);
      }
    },
    render() {
      this.widgetId = window.turnstile.render(this.$refs.container, {
        sitekey: this.siteKey,
        theme: this.theme,
        size: this.size,
        language: this.language,
        action: this.action,
        appearance: this.appearance,
        callback: this.onSuccess,
        "error-callback": this.onError,
        "expired-callback": this.onExpired,
        "unsupported-callback": this.onUnsupported
      });
    },
    removeWidget() {
      if (this.widgetId !== null && window.turnstile) {
        window.turnstile.remove(this.widgetId);
      }
      this.widgetId = null;
    },
    reset() {
      this.$emit("update:modelValue", "");
      this.clearResetTimer();
      if (this.widgetId !== null && window.turnstile) {
        window.turnstile.reset(this.widgetId);
      } else {
        this.init();
      }
    },
    clearResetTimer() {
      clearTimeout(this.resetTimer);
      this.resetTimer = null;
    },
    onSuccess(token) {
      this.errorCode = "";
      this.$emit("update:modelValue", token);
      // El token de Turnstile caduca a los 300s: se renueva antes de vencer.
      this.clearResetTimer();
      this.resetTimer = setTimeout(() => this.reset(), RESET_INTERVAL);
    },
    onError(code) {
      this.errorCode = code || "";
      this.$emit("update:modelValue", "");
      this.$emit("error", code);
    },
    onExpired() {
      this.$emit("expired");
      this.reset();
    },
    onUnsupported() {
      this.loadError = true;
      this.$emit("unsupported");
    }
  }
};
</script>
